// Behavior for the PDP "material sample form" block (sections/collapsible-content.liquid,
// block type 'material_sample_form'): step 1/2 material type + color picker, the
// collapsed address fields in step 3, and the optional anchor link / post-success bits
// that block also renders. Runs for every .material-sample-form on the page, so more
// than one block instance per page is supported.
(function () {
  // Adds/removes the `required` (+ aria-required) attributes that mark a field as
  // mandatory, and clears any leftover invalid-state styling when a field stops being
  // required (e.g. the address fields once the customer clears their color selection).
  function setRequired(field, isRequired) {
    if (!field) return;
    if (isRequired) {
      field.setAttribute('required', '');
      field.setAttribute('aria-required', 'true');
    } else {
      field.removeAttribute('required');
      field.removeAttribute('aria-required');
      field.removeAttribute('aria-invalid');
    }
  }

  // Wires up steps 1 + 2 (material type & color selection) and the step 3 address
  // disclosure for a single block instance. Liquid-side data (counts, markup, flags)
  // is passed in via data-* attributes on materialSampleFormRoot rather than templated
  // into this file, since this script is a static asset shared by every instance.
  function initMaterialSampleForm(materialSampleFormRoot) {
    const allowMultipleMaterialTypes = materialSampleFormRoot.dataset.allowMultipleMaterialTypes === 'true';
    const maxSampleCount = parseInt(materialSampleFormRoot.dataset.maxSampleCount, 10) || 0;
    const removeIconMarkup = materialSampleFormRoot.dataset.removeIconMarkup || '';

    const materialTypeButtons = Array.prototype.slice.call(materialSampleFormRoot.querySelectorAll('.material-sample-form__material-type'));
    const materialTypeDescriptionGroups = Array.prototype.slice.call(materialSampleFormRoot.querySelectorAll('.material-sample-form__material-type-description-group'));
    const materialTypeDescriptionToggles = Array.prototype.slice.call(materialSampleFormRoot.querySelectorAll('.material-sample-form__material-type-description-toggle'));
    const colorGroups = Array.prototype.slice.call(materialSampleFormRoot.querySelectorAll('.material-sample-form__color-group'));
    const searchInput = materialSampleFormRoot.querySelector('.material-sample-form__search-input');
    const selectedColorChipList = materialSampleFormRoot.querySelector('.material-sample-form__selected-color-chip-list');
    const selectedColorCount = materialSampleFormRoot.querySelector('.material-sample-form__selected-color-count');
    const clearSelectionButton = materialSampleFormRoot.querySelector('.material-sample-form__clear-selection-button');
    const sampleLimitReachedMessage = materialSampleFormRoot.querySelector('.material-sample-form__sample-limit-reached-message');
    const colorWishInput = materialSampleFormRoot.querySelector('.material-sample-form__color-wish-input');
    const colorCodesInput = materialSampleFormRoot.querySelector('.material-sample-form__color-codes-input');

    // Maps each material type handle to a Map of selected colorId -> { label, materialTypeName, imageUrl }
    const selectedColorsByMaterialType = {};
    materialTypeButtons.forEach(function(button) {
      selectedColorsByMaterialType[button.dataset.materialType] = new Map();
    });

    const initiallySelectedButton = materialSampleFormRoot.querySelector('.material-sample-form__material-type--selected') || materialTypeButtons[0];
    let activeMaterialType = initiallySelectedButton ? initiallySelectedButton.dataset.materialType : null;
    let searchQuery = '';
    let isMaterialTypeDescriptionExpanded = false;

    const updateFormMode = setupFormModeSwitching(materialSampleFormRoot);

    function getTotalSelectedColorCount() {
      let count = 0;
      Object.keys(selectedColorsByMaterialType).forEach(function(materialTypeHandle) {
        count += selectedColorsByMaterialType[materialTypeHandle].size;
      });
      return count;
    }

    function hasReachedSampleLimit() {
      return maxSampleCount > 0 && getTotalSelectedColorCount() >= maxSampleCount;
    }

    // The color grid for the currently active material type (step 1 selection);
    // every other material type's grid stays in the DOM but hidden.
    function getActiveColorGroup() {
      return colorGroups.filter(function(colorGroup) {
        return colorGroup.dataset.materialType === activeMaterialType;
      })[0];
    }

    // The "Mehr zum Material erfahren" toggle is shared across all material types, so
    // its expanded/collapsed state is global rather than per material type.
    function renderMaterialTypeDescriptionExpandedState() {
      materialTypeDescriptionToggles.forEach(function(descriptionToggle) {
        const description = materialSampleFormRoot.querySelector('#' + descriptionToggle.getAttribute('aria-controls'));
        if (!description) return;
        descriptionToggle.setAttribute('aria-expanded', isMaterialTypeDescriptionExpanded ? 'true' : 'false');
        description.hidden = !isMaterialTypeDescriptionExpanded;
      });
    }

    // Applies the search filter and current selection/disabled state to every color
    // swatch in the active material type's grid.
    function renderActiveColorGroup() {
      const activeColorGroup = getActiveColorGroup();
      if (!activeColorGroup) return;

      const colorGrid = activeColorGroup.querySelector('.material-sample-form__color-grid');
      const noResultsMessage = activeColorGroup.querySelector('.material-sample-form__no-results-message');
      const colorButtons = Array.prototype.slice.call(colorGrid.querySelectorAll('.material-sample-form__color'));
      const normalizedQuery = searchQuery.trim().toLowerCase();
      const atLimit = hasReachedSampleLimit();
      let visibleColorCount = 0;

      colorButtons.forEach(function(colorButton) {
        const colorLabel = (colorButton.dataset.colorLabel || '').toLowerCase();
        const matchesQuery = !normalizedQuery || colorLabel.indexOf(normalizedQuery) !== -1;
        colorButton.hidden = !matchesQuery;
        if (matchesQuery) visibleColorCount++;

        const isSelected = selectedColorsByMaterialType[activeMaterialType].has(colorButton.dataset.colorId);
        colorButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        colorButton.disabled = !isSelected && atLimit;
      });

      if (noResultsMessage) noResultsMessage.hidden = visibleColorCount !== 0;
    }

    // Shows/hides the "max samples reached" notice based on block.settings.max_samples.
    function renderSampleLimitReachedMessage() {
      if (!sampleLimitReachedMessage) return;
      if (maxSampleCount > 0 && getTotalSelectedColorCount() >= maxSampleCount) {
        sampleLimitReachedMessage.hidden = false;
        sampleLimitReachedMessage.textContent = 'Sie haben die maximale Anzahl von ' + maxSampleCount + ' Mustern erreicht.';
      } else {
        sampleLimitReachedMessage.hidden = true;
      }
    }

    // Rebuilds the "Ihre Auswahl" chip list from selectedColorsByMaterialType, including
    // the per-chip remove button, and refreshes the count/limit/clear-button state.
    function renderSelectionSummary() {
      const materialTypeHandlesWithSelections = Object.keys(selectedColorsByMaterialType).filter(function(materialTypeHandle) {
        return selectedColorsByMaterialType[materialTypeHandle].size > 0;
      });

      if (!materialTypeHandlesWithSelections.length) {
        selectedColorChipList.innerHTML = '<span class="material-sample-form__selected-color-chip-list-empty-message">Noch keine Farbe ausgewählt.</span>';
      } else {
        let chipListMarkup = '';
        materialTypeHandlesWithSelections.forEach(function(materialTypeHandle) {
          selectedColorsByMaterialType[materialTypeHandle].forEach(function(selectedColor, colorId) {
            chipListMarkup += '<button type="button" class="material-sample-form__selected-color-chip" data-material-type="' + materialTypeHandle + '" data-color-id="' + colorId + '" title="Entfernen">' +
              '<span class="material-sample-form__selected-color-chip-swatch" style="background-image:url(' + selectedColor.imageUrl + ')"></span>' +
              '<span>' + (allowMultipleMaterialTypes ? selectedColor.materialTypeName + ': ' : '') + selectedColor.label + '</span>' +
              '<span class="material-sample-form__selected-color-chip-remove-icon">' + removeIconMarkup + '</span>' +
            '</button>';
          });
        });
        selectedColorChipList.innerHTML = chipListMarkup;

        Array.prototype.slice.call(selectedColorChipList.querySelectorAll('.material-sample-form__selected-color-chip')).forEach(function(chip) {
          chip.addEventListener('click', function() {
            const materialTypeHandle = chip.dataset.materialType;
            const colorId = chip.dataset.colorId;
            selectedColorsByMaterialType[materialTypeHandle].delete(colorId);
            if (materialTypeHandle === activeMaterialType) renderActiveColorGroup();
            renderSelectionSummary();
            syncColorWishInput();
            syncColorCodesInput();
          });
        });
      }

      const totalSelectedColorCount = getTotalSelectedColorCount();
      if (selectedColorCount) {
        selectedColorCount.hidden = totalSelectedColorCount === 0;
        selectedColorCount.textContent = totalSelectedColorCount + ' Muster';
      }
      if (clearSelectionButton) clearSelectionButton.hidden = totalSelectedColorCount === 0;

      renderSampleLimitReachedMessage();
      updateFormMode(totalSelectedColorCount > 0);
    }

    function resizeColorWishInputToFitContent() {
      if (!colorWishInput) return;
      colorWishInput.style.height = 'auto';
      colorWishInput.style.height = colorWishInput.scrollHeight + 'px';
    }

    // Writes the human-readable "<Stoffart> (<Farbe>, <Farbe>)" summary into the
    // read-only "Farbwunsch" textarea (step 3) that actually gets submitted with the form.
    function syncColorWishInput() {
      if (!colorWishInput) return;
      const wishPartsByMaterialType = [];
      Object.keys(selectedColorsByMaterialType).forEach(function(materialTypeHandle) {
        const selectedColors = selectedColorsByMaterialType[materialTypeHandle];
        if (!selectedColors.size) return;
        const colorLabels = [];
        let materialTypeName = '';
        selectedColors.forEach(function(selectedColor) {
          colorLabels.push(selectedColor.label);
          materialTypeName = selectedColor.materialTypeName;
        });
        wishPartsByMaterialType.push(materialTypeName + ' (' + colorLabels.join(', ') + ')');
      });
      colorWishInput.value = wishPartsByMaterialType.join(', ');
      resizeColorWishInputToFitContent();
    }

    // Fills the hidden "Farbcodes" field with the color codes of all selected colors.
    // Colors that have a code are listed first (comma-separated); colors without one are
    // appended as a note so the recipient knows which codes are still missing.
    function syncColorCodesInput() {
      if (!colorCodesInput) return;
      const withCode = [];
      const withoutCode = [];
      Object.keys(selectedColorsByMaterialType).forEach(function(materialTypeHandle) {
        selectedColorsByMaterialType[materialTypeHandle].forEach(function(selectedColor) {
          if (selectedColor.colorCode) {
            withCode.push(selectedColor.colorCode);
          } else {
            withoutCode.push(selectedColor.backendName || selectedColor.label);
          }
        });
      });
      if (!withCode.length && !withoutCode.length) {
        colorCodesInput.value = '';
        return;
      }
      const parts = [];
      if (withCode.length) parts.push(withCode.join(', ') + (withoutCode.length ? '.' : ''));
      if (withoutCode.length) parts.push('Für folgende Farben ist kein Farbcode eingepflegt: ' + withoutCode.join(', '));
      colorCodesInput.value = parts.join('\n');
    }

    // Switches step 1's active material type and updates everything that depends on it
    // (step 2's color grid, descriptions, search, summary, Farbwunsch text). When
    // allowMultipleMaterialTypes is off, switching material types also clears any
    // colors picked for the other material types, since only one can be submitted.
    function setActiveMaterialType(materialTypeHandle) {
      if (!allowMultipleMaterialTypes) {
        Object.keys(selectedColorsByMaterialType).forEach(function(otherMaterialTypeHandle) {
          if (otherMaterialTypeHandle !== materialTypeHandle) {
            selectedColorsByMaterialType[otherMaterialTypeHandle].clear();
          }
        });
      }

      activeMaterialType = materialTypeHandle;

      materialTypeButtons.forEach(function(button) {
        const isActive = button.dataset.materialType === materialTypeHandle;
        button.classList.toggle('material-sample-form__material-type--selected', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      materialTypeDescriptionGroups.forEach(function(descriptionGroup) {
        descriptionGroup.classList.toggle('hidden', descriptionGroup.dataset.materialType !== materialTypeHandle);
      });

      colorGroups.forEach(function(colorGroup) {
        colorGroup.classList.toggle('hidden', colorGroup.dataset.materialType !== materialTypeHandle);
      });

      searchQuery = '';
      if (searchInput) searchInput.value = '';

      renderActiveColorGroup();
      renderSelectionSummary();
      syncColorWishInput();
      syncColorCodesInput();
    }

    materialTypeButtons.forEach(function(button) {
      button.addEventListener('click', function() {
        setActiveMaterialType(button.dataset.materialType);
      });
    });

    materialTypeDescriptionToggles.forEach(function(descriptionToggle) {
      descriptionToggle.addEventListener('click', function() {
        isMaterialTypeDescriptionExpanded = !isMaterialTypeDescriptionExpanded;
        renderMaterialTypeDescriptionExpandedState();
      });
    });

    colorGroups.forEach(function(colorGroup) {
      Array.prototype.slice.call(colorGroup.querySelectorAll('.material-sample-form__color')).forEach(function(colorButton) {
        colorButton.addEventListener('click', function() {
          if (colorButton.disabled) return;
          const materialTypeHandle = colorGroup.dataset.materialType;
          const colorId = colorButton.dataset.colorId;
          const selectedColors = selectedColorsByMaterialType[materialTypeHandle];

          if (selectedColors.has(colorId)) {
            selectedColors.delete(colorId);
          } else {
            if (hasReachedSampleLimit()) return;
            selectedColors.set(colorId, {
              label: colorButton.dataset.colorLabel,
              backendName: colorButton.dataset.colorBackendName || '',
              materialTypeName: colorButton.dataset.materialTypeName,
              imageUrl: colorButton.dataset.colorImageUrl,
              colorCode: colorButton.dataset.colorCode || ''
            });
          }

          renderActiveColorGroup();
          renderSelectionSummary();
          syncColorWishInput();
          syncColorCodesInput();
        });
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', function(event) {
        searchQuery = event.target.value;
        renderActiveColorGroup();
      });
    }

    if (clearSelectionButton) {
      clearSelectionButton.addEventListener('click', function() {
        Object.keys(selectedColorsByMaterialType).forEach(function(materialTypeHandle) {
          selectedColorsByMaterialType[materialTypeHandle].clear();
        });
        renderActiveColorGroup();
        renderSelectionSummary();
        syncColorWishInput();
        syncColorCodesInput();
      });
    }

    if (activeMaterialType) setActiveMaterialType(activeMaterialType);

    // Step 3's name/address fields are collapsed by default to keep the form short;
    // this just toggles the [hidden] attribute and the aria-expanded state on the button.
    const addressToggle = materialSampleFormRoot.querySelector('.material-sample-form__address-toggle');
    const addressFields = materialSampleFormRoot.querySelector('.material-sample-form__address-fields');
    if (addressToggle && addressFields) {
      addressToggle.addEventListener('click', function() {
        const isExpanded = addressToggle.getAttribute('aria-expanded') === 'true';
        addressToggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        addressFields.hidden = isExpanded;
        addressToggle.removeAttribute('aria-invalid');
      });
    }

    setupRequiredFieldValidation(materialSampleFormRoot, addressToggle, addressFields);

    // WebMCP bridge: prepares the existing visible sample-request UI from structured
    // agent input. It deliberately never submits the contact form; the customer keeps
    // the final confirmation by clicking the visible "Senden" button themselves.
    materialSampleFormRoot.__sdWebMcpPrepareMaterialSample = function(payload) {
      payload = payload || {};

      function normalizeAgentText(value) {
        return String(value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/ß/g, 'ss')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      function setAgentField(selector, value) {
        if (value === undefined || value === null || value === '') return;
        const field = materialSampleFormRoot.querySelector(selector);
        if (!field) return;
        field.value = String(value);
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (payload.product_url) {
        try {
          const target = new URL(payload.product_url, window.location.origin);
          if (target.origin !== window.location.origin || target.pathname !== window.location.pathname) {
            return JSON.stringify({
              status: 'wrong_product',
              message: 'Die angegebene product_url gehoert nicht zur aktuell geoeffneten Produktseite.'
            });
          }
        } catch (error) {
          return JSON.stringify({ status: 'invalid_product_url', message: 'Die product_url ist ungueltig.' });
        }
      }

      const requestedMaterial = normalizeAgentText(payload.material_type);
      const materialButton = materialTypeButtons.filter(function(button) {
        const handle = normalizeAgentText(button.dataset.materialType);
        const label = normalizeAgentText(button.textContent);
        return handle === requestedMaterial || label === requestedMaterial ||
          (requestedMaterial && handle.indexOf(requestedMaterial) !== -1) ||
          (requestedMaterial && label.indexOf(requestedMaterial) !== -1) ||
          (handle && requestedMaterial.indexOf(handle) !== -1);
      })[0];

      if (!materialButton) {
        return JSON.stringify({
          status: 'material_not_found',
          requested_material: payload.material_type || '',
          available_materials: materialTypeButtons.map(function(button) {
            const label = button.querySelector('.material-sample-form__material-type-name');
            return label ? label.textContent.trim() : button.textContent.trim();
          })
        });
      }

      Object.keys(selectedColorsByMaterialType).forEach(function(materialTypeHandle) {
        selectedColorsByMaterialType[materialTypeHandle].clear();
      });
      setActiveMaterialType(materialButton.dataset.materialType);

      const requestedColors = Array.isArray(payload.colors) ? payload.colors : [];
      const activeColorGroup = getActiveColorGroup();
      const colorButtons = activeColorGroup ? Array.prototype.slice.call(activeColorGroup.querySelectorAll('.material-sample-form__color')) : [];
      const matchedColors = [];
      const missingColors = [];

      requestedColors.forEach(function(requestedColor) {
        if (hasReachedSampleLimit()) {
          missingColors.push(String(requestedColor) + ' (Musterlimit erreicht)');
          return;
        }
        const normalizedRequestedColor = normalizeAgentText(requestedColor);
        let match = colorButtons.filter(function(button) {
          return [button.dataset.colorLabel, button.dataset.colorBackendName, button.dataset.colorCode]
            .some(function(candidate) { return normalizeAgentText(candidate) === normalizedRequestedColor; });
        })[0];
        if (!match) {
          match = colorButtons.filter(function(button) {
            return [button.dataset.colorLabel, button.dataset.colorBackendName, button.dataset.colorCode]
              .some(function(candidate) {
                const normalizedCandidate = normalizeAgentText(candidate);
                return normalizedRequestedColor && normalizedCandidate &&
                  (normalizedCandidate.indexOf(normalizedRequestedColor) !== -1 || normalizedRequestedColor.indexOf(normalizedCandidate) !== -1);
              });
          })[0];
        }
        if (!match) {
          missingColors.push(String(requestedColor));
          return;
        }

        const colorId = match.dataset.colorId;
        selectedColorsByMaterialType[activeMaterialType].set(colorId, {
          label: match.dataset.colorLabel,
          backendName: match.dataset.colorBackendName || '',
          materialTypeName: match.dataset.materialTypeName,
          imageUrl: match.dataset.colorImageUrl,
          colorCode: match.dataset.colorCode || ''
        });
        matchedColors.push(match.dataset.colorLabel || String(requestedColor));
      });

      renderActiveColorGroup();
      renderSelectionSummary();
      syncColorWishInput();
      syncColorCodesInput();

      setAgentField('[autocomplete="given-name"]', payload.first_name);
      setAgentField('[autocomplete="family-name"]', payload.last_name);
      setAgentField('[autocomplete="email"]', payload.email);
      setAgentField('[autocomplete="tel"]', payload.phone);
      setAgentField('[autocomplete="address-line1"]', payload.address);
      setAgentField('[autocomplete="address-line2"]', payload.address2);
      setAgentField('[autocomplete="address-level2"]', payload.city);
      setAgentField('[autocomplete="postal-code"]', payload.postal_code);
      setAgentField('[autocomplete="country-name"]', payload.country);
      setAgentField('.material-sample-form__comment-input', payload.comment);

      const accordionItem = materialSampleFormRoot.closest('.collapsible-content__item');
      if (accordionItem) {
        accordionItem.classList.add('active');
        const answer = accordionItem.querySelector(':scope > .collapsible-content__answer');
        if (answer) answer.style.display = 'block';
      }
      if (addressToggle && addressFields && matchedColors.length) {
        addressToggle.setAttribute('aria-expanded', 'true');
        addressFields.hidden = false;
      }

      const form = materialSampleFormRoot.querySelector('form');
      const missingRequiredFields = form ? Array.prototype.slice.call(form.querySelectorAll('[required]')).filter(function(field) {
        return !field.checkValidity();
      }).map(function(field) {
        return field.getAttribute('placeholder') || field.getAttribute('autocomplete') || field.name || field.id;
      }) : [];

      materialSampleFormRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });

      return JSON.stringify({
        status: matchedColors.length ? 'prepared' : 'needs_color',
        material: (materialButton.querySelector('.material-sample-form__material-type-name') || materialButton).textContent.trim(),
        matched_colors: matchedColors,
        missing_colors: missingColors,
        missing_required_fields: missingRequiredFields,
        submitted: false,
        message: 'Die Materialmuster-Anfrage ist sichtbar vorbereitet. Der Nutzer muss Senden selbst anklicken.'
      });
    };
  }

  // A submission with no color picked in step 2 is treated as a general question (no
  // address needed, just a "Name" field); one with at least one color picked is treated
  // as a sample request (address needed, split into Vorname/Nachname instead of "Name").
  // Switches the step 3 fields between those two modes and keeps the hidden "Betreff"
  // field in sync, so the merchant's email makes it obvious which kind of submission it
  // was. Called every time the color selection changes (see renderSelectionSummary).
  // Returns the updateFormMode(hasSelection) function; it no-ops if the mode hasn't
  // actually changed, so toggling colors within the same mode doesn't reset the address
  // dropdown's open/closed state or wipe out anything the customer already typed.
  function setupFormModeSwitching(materialSampleFormRoot) {
    const nameField = materialSampleFormRoot.querySelector('.material-sample-form__name-field');
    const nameInput = nameField ? nameField.querySelector('input') : null;
    const addressGroup = materialSampleFormRoot.querySelector('.material-sample-form__address-group');
    const addressRequiredFields = Array.prototype.slice.call(materialSampleFormRoot.querySelectorAll('.material-sample-form__address-required-field'));
    const colorWishField = materialSampleFormRoot.querySelector('.material-sample-form__color-wish-field');
    const phoneInput = materialSampleFormRoot.querySelector('.material-sample-form__phone-input');
    const commentInput = materialSampleFormRoot.querySelector('.material-sample-form__comment-input');
    const commentLabel = materialSampleFormRoot.querySelector('.material-sample-form__comment-label');
    const subjectInput = materialSampleFormRoot.querySelector('.material-sample-form__subject-input');
    const contactStepLabel = materialSampleFormRoot.querySelector('.material-sample-form__step--contact');

    const questionLabel = materialSampleFormRoot.dataset.questionLabel || '';
    const commentLabelText = materialSampleFormRoot.dataset.commentLabel || '';
    const subjectQuestion = materialSampleFormRoot.dataset.subjectQuestion || '';
    const subjectSampleRequest = materialSampleFormRoot.dataset.subjectSampleRequest || '';
    const phoneRequiredInQuestionMode = materialSampleFormRoot.dataset.phoneRequiredInQuestionMode === 'true';

    function getSampleRequestSubject() {
      const productTitle = materialSampleFormRoot.dataset.productTitle || '';
      const currentSku = materialSampleFormRoot.dataset.currentSku || '';
      return [subjectSampleRequest, productTitle, currentSku ? 'SKU ' + currentSku : '']
        .filter(Boolean)
        .join(' – ');
    }

    let isSampleRequestMode = null;

    return function updateFormMode(hasSelection) {
      if (hasSelection === isSampleRequestMode) return;
      isSampleRequestMode = hasSelection;

      if (nameField) nameField.hidden = hasSelection;
      setRequired(nameInput, !hasSelection);

      if (contactStepLabel) contactStepLabel.hidden = !hasSelection;

      if (addressGroup) addressGroup.hidden = !hasSelection;
      addressRequiredFields.forEach(function(field) {
        setRequired(field, hasSelection);
      });

      if (colorWishField) colorWishField.hidden = !hasSelection;

      // Always required for a sample request; in question mode, follows the block setting.
      setRequired(phoneInput, hasSelection || phoneRequiredInQuestionMode);

      const commentText = hasSelection ? commentLabelText : questionLabel;
      if (commentInput) {
        commentInput.placeholder = commentText;
        commentInput.name = 'contact[' + commentText + ']';
        // Required as "Frage" (no color picked yet, so this is the customer's only way to
        // ask something); optional as "Kommentar" once it's a sample request.
        setRequired(commentInput, !hasSelection);
      }
      if (commentLabel) commentLabel.textContent = commentText;

      if (subjectInput) subjectInput.value = hasSelection ? getSampleRequestSubject() : subjectQuestion;
    };
  }

  // Replaces native browser validation (form has novalidate) with a custom check, because
  // a required field hidden inside the collapsed address dropdown can't be focused/shown
  // by the browser's own "please fill out this field" bubble, which made submit attempts
  // silently fail with required address fields. Marks every empty/invalid required field
  // with aria-invalid (styled red in component-material-sample-form.css), opens the
  // address dropdown if one of the invalid fields lives in there, and focuses the first
  // invalid field. Required fields are queried fresh every time (rather than cached once)
  // since setupFormModeSwitching adds/removes `required` dynamically.
  function setupRequiredFieldValidation(materialSampleFormRoot, addressToggle, addressFields) {
    const form = materialSampleFormRoot.querySelector('form');
    if (!form) return;

    // Belt-and-suspenders: the Liquid form tag already renders novalidate, but setting it
    // here too means this still works even if that attribute is ever lost (e.g. a theme
    // editor section reload that doesn't preserve it), since native validation would
    // otherwise silently win a race against our checks below.
    form.setAttribute('novalidate', '');

    Array.prototype.slice.call(form.querySelectorAll('input, textarea')).forEach(function(field) {
      field.addEventListener('input', function() {
        if (field.checkValidity()) field.removeAttribute('aria-invalid');
      });
    });

    function getInvalidFields() {
      return Array.prototype.slice.call(form.querySelectorAll('[required]')).filter(function(field) {
        return !field.checkValidity();
      });
    }

    function clearInvalidState() {
      Array.prototype.slice.call(form.querySelectorAll('[aria-invalid]')).forEach(function(field) {
        field.removeAttribute('aria-invalid');
      });
      if (addressToggle) addressToggle.removeAttribute('aria-invalid');
    }

    function showInvalidState(invalidFields) {
      invalidFields.forEach(function(field) {
        field.setAttribute('aria-invalid', 'true');
      });

      const hasInvalidAddressField = addressFields && invalidFields.some(function(field) {
        return addressFields.contains(field);
      });

      if (hasInvalidAddressField && addressToggle) {
        addressToggle.setAttribute('aria-expanded', 'true');
        addressFields.hidden = false;
        addressToggle.setAttribute('aria-invalid', 'true');
      }

      invalidFields[0].focus();
      invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Covers Enter-key submission (which fires 'submit' directly, with no button click).
    form.addEventListener('submit', function(event) {
      const invalidFields = getInvalidFields();
      clearInvalidState();
      if (!invalidFields.length) return;
      event.preventDefault();
      showInvalidState(invalidFields);
    });

    // Also validate directly on the submit button's click, in the capture phase, so this
    // runs - and can fully short-circuit everything else via stopImmediatePropagation -
    // before any other click listener already on the button. That matters because
    // Shopify's invisible CAPTCHA protection (if enabled) hooks the submit button's click
    // and calls the imperative form.submit() once the challenge passes, which per spec
    // does NOT fire a 'submit' event at all - bypassing the listener above entirely and
    // submitting the form even though it's invalid.
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submitButton) {
      submitButton.addEventListener('click', function(event) {
        const invalidFields = getInvalidFields();
        clearInvalidState();
        if (!invalidFields.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        showInvalidState(invalidFields);
      }, { capture: true });
    }
  }

  // Optionally injects a "<label> →" jump link elsewhere on the PDP (next to the color
  // swatches or the extras/options list) that scrolls down to this block, based on the
  // block's anchor_type/anchor_link_text settings. No-op unless that setting is set.
  function setupAnchorLink(materialSampleFormRoot) {
    const anchorType = materialSampleFormRoot.dataset.anchorType;
    const anchorLabel = materialSampleFormRoot.dataset.anchorLabel;
    if ((anchorType !== 'colors' && anchorType !== 'extras') || !anchorLabel) return;

    function createAnchor(href) {
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.textContent = anchorLabel + ' →';
      anchor.classList.add(anchorType + '-anchor-link', 'anchor-link');
      return anchor;
    }

    if (anchorType === 'colors') {
      document.addEventListener('DOMContentLoaded', function() {
        const optionWrapper = document.querySelector('.product-form__controls--colors');
        if (!optionWrapper) return;
        const legend = optionWrapper.querySelector('legend');
        if (legend) legend.appendChild(createAnchor('#more-colors'));
      });
    } else if (anchorType === 'extras') {
      function onLabelContainerReady(optionWrapper) {
        optionWrapper.appendChild(createAnchor('#more-extras'));
      }

      document.addEventListener('DOMContentLoaded', function() {
        const existing = document.querySelector('.po-label-container');
        if (existing) {
          onLabelContainerReady(existing);
          return;
        }

        const observer = new MutationObserver(function(_, obs) {
          const optionWrapper = document.querySelector('.po-label-container');
          if (optionWrapper) {
            obs.disconnect();
            onLabelContainerReady(optionWrapper);
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
      });
    }
  }

  // Opens the material accordion and scrolls it below the sticky header for every
  // existing or dynamically injected #more-colors link. A delegated handler also
  // covers buttons rendered by other sections of the product template.
  function setupMaterialSampleAnchorNavigation() {
    if (document.documentElement.dataset.materialSampleAnchorNavigationBound === 'true') return;
    document.documentElement.dataset.materialSampleAnchorNavigationBound = 'true';

    document.addEventListener('click', function(event) {
      const anchor = event.target.closest('a[href$="#more-colors"]');
      if (!anchor) return;

      const configurator = document.querySelector('sofa-configurator');
      const isConfiguratorLink = !anchor.closest('.product__link');
      if (isConfiguratorLink && configurator && typeof configurator.open === 'function') {
        event.preventDefault();
        event.stopImmediatePropagation();
        configurator.open();
        return;
      }

      const target = document.getElementById('more-colors');
      if (!target) return;

      event.preventDefault();
      // This theme has a second delegated image-with-text click handler that otherwise
      // scrolls back to the CTA immediately after this handler has moved to the form.
      // Keep this scoped to material-sample anchors so unrelated section links still work.
      event.stopPropagation();
      target.classList.add('active');
      const answer = target.querySelector(':scope > .collapsible-content__answer');
      if (answer) answer.style.display = 'block';

      requestAnimationFrame(function() {
        const stickyHeader = document.querySelector('.shopify-section-header-sticky, sticky-header, .header-wrapper');
        const headerOffset = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
        const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }, true);
  }

  // After Shopify redirects back from a successful submit, the success message is
  // rendered hidden (style="display:none") to avoid a flash before the redirect; this
  // reveals it once the URL hash confirms we're back on this form.
  function showPostSuccessMessage() {
    const formStatus = document.querySelector('[data-form-id="contact-form--accordion"]');
    if (formStatus && window.location.hash === '#contact-form--accordion') {
      formStatus.style.display = '';
    }
  }

  document.addEventListener('DOMContentLoaded', showPostSuccessMessage);

  // Fills and keeps in sync the hidden "Variante" field in every material sample form on
  // the page. Variant data is read from the <script type="application/json"> already
  // embedded inside the theme's variant-selects / variant-radios custom element.
  // The formatted value is: "{variant.title} – SKU: {variant.sku}" (SKU omitted if blank).
  //
  // Important: we do NOT read input[name="id"] to detect the active variant. Dawn updates
  // that input asynchronously (after a section-render fetch), so reading it in a change
  // listener yields the previous variant. Instead we read the checked radio / select values
  // directly from the variant-selects / variant-radios element — those are already in their
  // new state at the moment the change event fires.
  function setupVariantTracking(materialSampleFormRoots) {
    if (!materialSampleFormRoots.length) return;

    function syncProductContext(root, variant) {
      const productTitle = root.dataset.productTitle || document.querySelector('main h1')?.textContent.trim() || '';
      const productUrl = root.dataset.productUrl || window.location.href.split('#')[0].split('?')[0];
      const sku = variant && variant.sku ? variant.sku : '';
      const variantTitle = variant && variant.title ? variant.title : '';

      root.dataset.productTitle = productTitle;
      root.dataset.currentSku = sku;

      const productTitleInput = root.querySelector('.material-sample-form__product-title-input');
      const productUrlInput = root.querySelector('.material-sample-form__product-url-input');
      const skuInput = root.querySelector('.material-sample-form__sku-input');
      const variantInput = root.querySelector('.material-sample-form__variant-input');
      const subjectInput = root.querySelector('.material-sample-form__subject-input');
      const colorWishInput = root.querySelector('.material-sample-form__color-wish-input');

      if (productTitleInput) productTitleInput.value = productTitle;
      if (productUrlInput) productUrlInput.value = productUrl;
      if (skuInput) skuInput.value = sku;
      if (variantInput) variantInput.value = variantTitle + (sku ? ' – SKU: ' + sku : '');
      if (subjectInput && colorWishInput && colorWishInput.value.trim()) {
        subjectInput.value = [root.dataset.subjectSampleRequest, productTitle, sku ? 'SKU ' + sku : '']
          .filter(Boolean)
          .join(' – ');
      }
    }

    const variantSelectorEl = document.querySelector('variant-selects, variant-radios');
    if (!variantSelectorEl) {
      materialSampleFormRoots.forEach(function(root) { syncProductContext(root, null); });
      return;
    }
    const variantDataScript = variantSelectorEl.querySelector('[type="application/json"]');
    if (!variantDataScript) {
      materialSampleFormRoots.forEach(function(root) { syncProductContext(root, variantSelectorEl.currentVariant || null); });
      return;
    }

    let variants;
    try { variants = JSON.parse(variantDataScript.textContent); } catch (e) {
      materialSampleFormRoots.forEach(function(root) { syncProductContext(root, variantSelectorEl.currentVariant || null); });
      return;
    }

    // Read the currently selected option values directly from the DOM elements
    // (works for both <select> and <input type="radio"> based pickers).
    function getSelectedOptions() {
      const selects = variantSelectorEl.querySelectorAll('select');
      if (selects.length) {
        return Array.prototype.slice.call(selects).map(function(s) { return s.value; });
      }
      return Array.prototype.slice.call(variantSelectorEl.querySelectorAll('[type="radio"]:checked')).map(function(r) { return r.value; });
    }

    function syncVariantInputs() {
      const options = getSelectedOptions();
      const variant = variants.filter(function(v) {
        return options.every(function(opt, i) { return v['option' + (i + 1)] === opt; });
      })[0] || variantSelectorEl.currentVariant || null;
      materialSampleFormRoots.forEach(function(root) { syncProductContext(root, variant); });
    }

    syncVariantInputs();

    document.addEventListener('change', function(event) {
      if (!event.target.closest('variant-selects, variant-radios')) return;
      syncVariantInputs();
    });
  }

  // Bootstrap every material sample form block on the page (loaded with `defer`, so the
  // DOM is already parsed by the time this runs).
  function initAllMaterialSampleForms() {
    const roots = Array.prototype.slice.call(document.querySelectorAll('.material-sample-form'));
    roots.forEach(function(root) {
      initMaterialSampleForm(root);
      setupAnchorLink(root);
    });
    setupVariantTracking(roots);

    window.SofaDreamsWebMCP = window.SofaDreamsWebMCP || {};
    if (roots.length && typeof roots[0].__sdWebMcpPrepareMaterialSample === 'function') {
      window.SofaDreamsWebMCP.prepareMaterialSample = function(payload) {
        return roots[0].__sdWebMcpPrepareMaterialSample(payload || {});
      };
    }

    try {
      const pendingMaterialSample = sessionStorage.getItem('sd_webmcp_material_sample_v1');
      if (pendingMaterialSample && roots.length && typeof roots[0].__sdWebMcpPrepareMaterialSample === 'function') {
        const pendingPayload = JSON.parse(pendingMaterialSample);
        let targetMatches = true;
        if (pendingPayload.product_url) {
          const targetUrl = new URL(pendingPayload.product_url, window.location.origin);
          targetMatches = targetUrl.origin === window.location.origin && targetUrl.pathname === window.location.pathname;
        }
        if (targetMatches) {
          sessionStorage.removeItem('sd_webmcp_material_sample_v1');
          setTimeout(function() {
            roots[0].__sdWebMcpPrepareMaterialSample(pendingPayload);
          }, 0);
        }
      }
    } catch (error) {
      console.warn('[Sofa Dreams WebMCP] Gespeicherte Materialmuster-Anfrage konnte nicht angewendet werden.', error);
    }
  }

  setupMaterialSampleAnchorNavigation();
  initAllMaterialSampleForms();

  // The theme editor replaces a section's markup via innerHTML on every settings change,
  // which silently drops any event listeners attached to it (and never re-executes the
  // <script src> tag, since injected <script> elements don't run) - without this, editing
  // this block's settings in the customizer would leave a freshly-rendered form with none
  // of the click handlers or submit validation from above wired up, so e.g. clicking
  // "Senden" would just fall through to a plain, unvalidated submit. Sibling
  // collapsible-content.js re-runs its own init the same way for the same reason.
  document.addEventListener('shopify:section:load', function() {
    initAllMaterialSampleForms();
    showPostSuccessMessage();
  });
})();
